<template>
  <div>
    <page-content-toolbar
      :title="$t('play.title')"
      :show-natural-language-switch="isAdvancedMode"
    />
    <loader v-if="applyingChanges"
      :label="$t('play.applyingChanges')"
    />
    <v-container
      v-else
    >
      <v-form ref="form" v-model="valid" :disabled="loadingSolutions || applyingChanges || !valid" @submit="proveAll" onsubmit="return false;">
        <v-card elevation="0">
          <v-card-title class="pt-0">{{ $t('play.chooseAScenario') }}</v-card-title>
          <v-card-actions>
            <input-scenario
              :available-elements.sync="decisionPolicy.elements"
              :elements.sync="scenarioElements"
              :enableElementCreation="false"
              @scenario-updated="onScenarioUpdated"
              :disabled="loadingSolutions"
              label="play.data"
              v-on="$listeners"
            />
          </v-card-actions>
          <v-card-text v-if="debugMode">
            <v-container class="pa-0">
              <v-row cols="12">
                <v-col cols="10" class="pa-0">
                  <input-options
                    :options.sync="options"
                    :available-options="decisionPolicy.options"
                    :disabled="loadingSolutions"
                  >
                  </input-options>
                </v-col>
                <v-col cols="2" class="py-0">
                  <v-text-field
                    v-model="limit"
                    type="number"
                    min="1"
                    max="20"
                    :label="$t('play.limitExplanations')"
                    hide-details="true"
                    :disabled="loadingSolutions"
                    ></v-text-field>
                  </v-col>
                </v-row>
            </v-container>
          </v-card-text>
          <template v-if="hasVariables">
              <v-card-text>
                <v-container fluid class="pa-0">
                  <v-row dense>
                    <v-col
                      v-for="(element, elementIndex) in scenarioElementsWithVariables"
                      :key="elementIndex"
                      sm="2"
                      md="3"
                    >
                      <v-card class="mx-auto" flat>
                        <v-card-title class="px-2 pt-1 pb-2">
                          <element-chip
                            :element="element"
                          />
                        </v-card-title>
                        <v-card-text>
                          <v-row>
                            <v-col
                              v-for="(_, propertyName, variableIndex) in element.variables"
                              :key="variableIndex"
                              sm="12"
                            >
                              <v-text-field
                                v-model="element.variables[propertyName]"
                                :label="propertyName"
                                hide-details="false"
                                dense
                                :disabled="loadingSolutions"
                                :rules="variableRules"
                              ></v-text-field>
                            </v-col>
                          </v-row>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>
            </template>
          <v-card-actions>
            <v-btn
              v-if="isAdmin()"
              class="raison-btn"
              @click="viewSource"
            >
              <v-icon>mdi-file-document-outline</v-icon>
              {{ $t('play.viewSource') }}
            </v-btn>
            <v-spacer></v-spacer>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  class="raison-btn"
                  type="submit"
                  :disabled="loadingSolutions || !valid"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon>mdi-play-box-outline</v-icon>
                  {{ $t('play.run') }}
                </v-btn>
              </template>
              <span>{{ $t('play.run') }}</span>
            </v-tooltip>
          </v-card-actions>
          <template v-if="results || loadingSolutions">
            <v-divider></v-divider>
            <loader v-if="loadingSolutions"
              :label="$t('play.loadingSolutions')"
            />
            <v-card v-else elevation="0">
              <v-card-title>{{ $t('play.solutions') }}</v-card-title>
              <v-container fluid class="pa-0">
                <v-row dense>
                  <v-col
                    v-for="(result, index) in results"
                    :key="index"
                  >
                    <v-card
                      class="mx-auto"
                    >
                      <template v-if="result.error">
                        <v-card-actions class="py-0 red lighten-4">
                          <v-list-item>
                            <v-list-item-icon class="mr-2">
                              <v-icon color="error">mdi-fire-circle</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content class="py-0">
                              <option-chip-2
                                :option="result.option"
                                :show-details="debugMode"
                              />
                            </v-list-item-content>
                          </v-list-item>
                          <v-spacer></v-spacer>
                          <v-btn
                            icon
                            @click="result.showExplanations = !result.showExplanations"
                          >
                            <v-icon>{{ result.showExplanations ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                          </v-btn>
                        </v-card-actions>
                        <v-expand-transition>
                          <div v-show="result.showExplanations">
                            <v-divider></v-divider>
                            <v-card-title>{{ $t('play.error') }}</v-card-title>
                            <v-card-subtitle>{{ result.errorMessage }}</v-card-subtitle>
                            <v-card-text>
                              {{ result.error }}
                            </v-card-text>
                          </div>
                        </v-expand-transition>
                      </template>
                      <template v-else>
                        <v-card-actions>
                          <v-list-item>
                            <v-list-item-icon class="mr-2">
                              <v-icon v-if="result.response.hasSolutions" color="success">mdi-check-circle-outline</v-icon>
                              <v-icon v-else color="error">mdi-close-circle-outline</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <option-chip-2
                                :option="result.option"
                                :show-details="debugMode"
                              />
                            </v-list-item-content>
                          </v-list-item>
                          <v-spacer></v-spacer>
                          <v-btn
                            icon
                            @click="result.showExplanations = !result.showExplanations"
                          >
                            <v-icon>{{ result.showExplanations ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                          </v-btn>
                        </v-card-actions>
                        <v-expand-transition v-if="isAdmin()">
                          <div v-show="result.showExplanations">
                            <v-divider></v-divider>
                            <template v-if="result.response.hasSolutions">
                              <v-card-subtitle>{{ $t('play.explanations') }}</v-card-subtitle>
                              <v-card-text class="text--secondary">
                                <ul
                                  v-for="(sol, solIndex) in result.response.solutions"
                                  :key="solIndex">
                                  <li>{{ sol }}</li>
                                </ul>
                              </v-card-text>
                            </template>
                            <v-card-text v-else>
                              {{ $t('play.thisIsNotASolution') }}
                            </v-card-text>
                          </div>
                        </v-expand-transition>
                        <v-expand-transition v-else>
                          <div v-show="result.showExplanations">
                            <v-divider></v-divider>
                            <v-card-text>
                              {{ $t('play.explanationNotYetAvailable') }}
                            </v-card-text>
                          </div>
                        </v-expand-transition>
                      </template>
                    </v-card>
                  </v-col>
                </v-row>
              </v-container>
            </v-card>
          </template>
        </v-card>
      </v-form>
    </v-container>
  </div>
</template>


<script>
import ElementChip from '@/components/global/ElementChip.vue'
import Loader from '@/components/global/Loader'
import PageContentToolbar from '@/components/layout/PageContentToolbar'
import InputOptions from '@/components/play/InputOptions'
import InputScenario from '@/components/editor/InputScenario'
import OptionChip2 from '@/components/global/OptionChip2'

import debugModeMixin from '@/mixins/debug-mode-mixin.js'
import userRightsMixin from '@/mixins/user-rights-mixin.js'

import decisionPolicyService from '@/services/decisionPolicy.js'
import helpService from '@/services/help.js'
import playService from '@/services/play.js'

export default {
  name: 'Play',

  components: { ElementChip, InputOptions, InputScenario, Loader, OptionChip2, PageContentToolbar },

  mixins: [ debugModeMixin, userRightsMixin ],

  data () {
    return {
        applyingChanges: true,
        decisionPolicy: { elements: [], options: [] },
        limit: 5,
        loadingSolutions: false,
        projectId: null,
        scenarioElements: [],
        results: [],
        options: [],
        valid: false,
        variableRules: [
          v => !!v || this.$t('global.required'),
          v => /^.{1,100}$/.test(v) || this.$t('editor.option.invalid'),
        ],
      };
    },

    watch: {
      results() {
        if(this.results?.length > 0) {
          helpService.setHelpPage(this.$store, 'run_with_results')
        }
        else {
          helpService.setHelpPage(this.$store, 'run')
        }
      },
    },

    computed: {
      hasVariables() {
        return this.scenarioElementsWithVariables.length > 0
      },
      scenarioElementsWithVariables() {
        return this.scenarioElements.filter(element => Object.entries(element.variables).length > 0)
      },
      isAdvancedMode () {
        return this.$store.state.mode === 'advanced'
      },
    },

    methods: {
      onScenarioUpdated(elements) {
        this.results = null
        this.scenarioElements = playService.addVariableSupportForAllElements(elements)
      },

      applyChanges() {
        // console.log('applyChanges')
        this.applyingChanges = true
        return playService.applyChanges(this.projectId)
          .then(() => {
            this.getDecisionPolicy()
          })
          .catch(err => {
            this.$emit('raison-event', { type: 'error', messages: [err] });
          })
          .finally(() => {
            this.applyingChanges = false
          });
      },

      getDecisionPolicy() {
        // console.log('getDecisionPolicy')
        return decisionPolicyService.getDecisionPolicyWithoutScenarios(this.projectId)
          .then(res => {
            this.decisionPolicy = res.data
            this.options = []
          }).catch(err => {
            this.$emit('raison-event', { type: 'error', messages: [err] });
          })
          .finally(() => {
            this.ready = true
            this.applyingChanges = false
          });
      },

      viewSource() {
        this.$router.push({ name: 'playSourceViewer', params: { id: this.projectId } });
      },

      proveAll(event) {
        event.preventDefault()
        this.loadingSolutions = true
        this.results = []
        // console.log("decisionPolicy", this.decisionPolicy.options)
        const scenario = this.scenarioElements.map(element => playService.toQueryElement(element))
        const options = this.options.length > 0 && this.debugMode ? this.options : this.decisionPolicy.options
        const allPromises = options.map(option => playService.prove(
            this.projectId,
            option.id,
            scenario,
            this.limit
          )
        )
        console.log('allPromises', allPromises)
        Promise.allSettled(allPromises)
          .then(responses => {
            // console.log('responses', responses)
            this.results = responses.map((response, index) => {
              if(response.status === 'fulfilled') {
                return {
                  option: options[index],
                  response: {
                    hasSolutions: response.value.data[0].isSolution,
                    solutions: response.value.data[0].explanation
                  },
                  showExplanations: false
                }
              }
              else { // status === 'rejected'
                // console.log('response', response)
                return {
                  option: options[index],
                  error: response.reason,
                  errorMessage: response.reason.response.data.message,
                  showExplanations: false
                }
              }
            })
          })
          .catch(err => {
            console.log('err', err)
            this.$emit('raison-event', { type: 'error', messages: [err] });
          })
          .finally(() => {
            this.results = playService.sortResultByErrorThenByHasSolutionThenByName(this.results)
            this.loadingSolutions = false
          })
      },
  },

  created () {
    this.results = null;
    this.projectId = this.$route.params.id;
    helpService.setHelpPage(this.$store, 'run')
    this.getUserInfo()
    if(this.isEditor(this.projectId)) {
      this.applyChanges()
    }
    else {
      this.getDecisionPolicy()
    }
  },
};
</script>

<style scoped>
/* li { list-style-type: "🔧 "; } */
</style>
